import { DatePicker, LoadingButton, LocalizationProvider } from "@mui/lab";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { StyledTextField } from "../../pages/login";
import { editPreviousExperienceSchema } from "../../schema";
import {
  useAddPreviousExperienceMutation,
  useEditPreviousExperienceMutation,
} from "../../services/profileAPI";
import { CustomDialog, CustomDialogTitle } from "../../ui";
import { isBefore, isSameDay } from "date-fns";

function EditPastExperiences({
  open,
  pastExperience,
  onClose,
}: {
  open?: boolean;
  pastExperience: any;
  onClose: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [startDate, setStartDate] = useState<Date | null>(
    pastExperience?.startDate || null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    pastExperience?.endDate || null
  );
  const [addPreviousExperience] = useAddPreviousExperienceMutation();
  const [editPreviousExperience] = useEditPreviousExperienceMutation();

  const formik = useFormik({
    initialValues: {
      company: pastExperience?.company || "",
      designation: pastExperience?.designation || "",
      brief: pastExperience?.brief || "",
    },
    validationSchema: editPreviousExperienceSchema,
    onSubmit: async (values) => {
      try {
        if (!startDate) {
          return enqueueSnackbar("please choose a start date", {
            variant: "error",
          });
        }
        let details;
        if (pastExperience?._id) {
          if (isSameDay(endDate!, new Date()) === false) {
            details = {
              previousWorkExperienceId: pastExperience?._id,
              company: values.company,
              designation: values.designation,
              brief: values.brief,
              startDate: startDate,
              endDate: endDate,
            };
          } else {
            details = {
              previousWorkExperienceId: pastExperience?._id,
              company: values.company,
              designation: values.designation,
              brief: values.brief,
              startDate: startDate,
            };
          }
          await editPreviousExperience(details);
        } else {
          if (isSameDay(endDate!, new Date()) === false) {
            details = {
              company: values.company,
              designation: values.designation,
              brief: values.brief,
              startDate: new Date(startDate),
              endDate: endDate,
            };
          } else {
            details = {
              company: values.company,
              designation: values.designation,
              brief: values.brief,
              startDate: new Date(startDate!),
            };
          }
          await addPreviousExperience(details).unwrap();
        }
        enqueueSnackbar("updated your past experience", {
          variant: "success",
        });
        onClose();
      } catch (error) {
        console.error(error);
        enqueueSnackbar("failed to update your past experience", {
          variant: "error",
        });
      }
    },
  });

  return (
    <>
      <CustomDialog open={open!} onClose={onClose} fullWidth>
        <CustomDialogTitle onClose={onClose}>
          <Typography>edit past experiences</Typography>
        </CustomDialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent dividers>
            <FormControl fullWidth variant="standard">
              <InputLabel shrink htmlFor="company" disableAnimation>
                company:
              </InputLabel>
              <StyledTextField
                id="company"
                name="company"
                fullWidth
                placeholder="company name"
                value={formik.values.company}
                onChange={formik.handleChange}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={
                  Boolean(formik.touched.company) && (
                    <>{formik.errors.company}</>
                  )
                }
              />
            </FormControl>

            <FormControl fullWidth variant="standard" margin="normal">
              <InputLabel shrink htmlFor="designation" disableAnimation>
                designation:
              </InputLabel>
              <StyledTextField
                id="designation"
                name="designation"
                fullWidth
                placeholder="designation"
                value={formik.values.designation}
                onChange={formik.handleChange}
                error={
                  formik.touched.designation &&
                  Boolean(formik.errors.designation)
                }
                helperText={
                  Boolean(formik.touched.designation) && (
                    <>{formik.errors.designation}</>
                  )
                }
              />
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack sx={{ mt: 2 }}>
                <InputLabel shrink htmlFor="startDate" disableAnimation>
                  start date:
                </InputLabel>
                <DatePicker
                  disableFuture
                  value={startDate}
                  inputFormat="dd/MM/yyyy"
                  cancelText="cancel"
                  okText="ok"
                  onChange={(newValue) => {
                    setStartDate(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                />
              </Stack>
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack sx={{ mt: 2 }}>
                <InputLabel shrink htmlFor="endDate" disableAnimation>
                  end date:
                </InputLabel>
                <DatePicker
                  disableFuture
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                  }}
                  inputFormat="dd/MM/yyyy"
                  shouldDisableDate={(day) => isBefore(day!, startDate!)}
                  todayText="present"
                  showTodayButton
                  cancelText="cancel"
                  okText="ok"
                  disabled={!startDate}
                  renderInput={(params) => <TextField {...params} />}
                />
              </Stack>
            </LocalizationProvider>

            <FormControl fullWidth variant="standard" margin="normal">
              <InputLabel shrink htmlFor="brief" disableAnimation>
                brief:
              </InputLabel>
              <StyledTextField
                id="brief"
                name="brief"
                fullWidth
                multiline
                rows={4}
                placeholder="talk about your work experience"
                value={formik.values.brief}
                onChange={formik.handleChange}
                error={formik.touched.brief && Boolean(formik.errors.brief)}
                helperText={
                  Boolean(formik.touched.brief) && <>{formik.errors.brief}</>
                }
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="secondary">
              cancel
            </Button>
            <LoadingButton
              variant="text"
              type="submit"
              loading={formik.isSubmitting}
            >
              update
            </LoadingButton>
          </DialogActions>
        </form>
      </CustomDialog>
    </>
  );
}

export default EditPastExperiences;
